import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '../../layout/main-layout';
import DuoModal from '../../components/ui/duo-modal';
import { APP_API_URL } from '../../utils';
import formatNumber, { defaultFilterDates, formatDateWithTime } from '../../utils/functions';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const PAYMENT_METHODS = [
  { label: 'Mobile Money', value: 1 },
  { label: 'Carte bancaire', value: 2 },
  { label: 'Virement', value: 3 },
  { label: 'Espèces', value: 4 },
  { label: 'Crédit', value: 5 },
  { label: 'Autre', value: 6 },
  { label: 'Chèques', value: 7 },
];

const getStartOfWeek = (date) => {
  const day = date.getDay();
  const diff = date.getDate() - day;
  const startOfWeek = new Date(date);
  startOfWeek.setDate(diff);
  startOfWeek.setHours(0, 0, 0, 0);
  return startOfWeek;
};

const getStartOfQuarter = (date) => {
  const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
  return new Date(date.getFullYear(), quarterStartMonth, 1);
};

const matchesPeriod = (itemDate, period) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  switch (period) {
    case 'today':
      return itemDate.toDateString() === today.toDateString();
    case 'yesterday':
      return itemDate.toDateString() === yesterday.toDateString();
    case 'this-week':
      return itemDate >= getStartOfWeek(today) && itemDate <= today;
    case 'this-month':
      return itemDate >= new Date(today.getFullYear(), today.getMonth(), 1) && itemDate <= today;
    case 'this-quarter':
      return itemDate >= getStartOfQuarter(today) && itemDate <= today;
    case 'this-year':
      return itemDate >= new Date(today.getFullYear(), 0, 1) && itemDate <= today;
    default:
      return true;
  }
};

export default function InstallmentOrder() {
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;

  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    search: '',
    period: '',
    ...defaultFilterDates(),
  }));
  const [draftFilters, setDraftFilters] = useState(() => ({
    period: '',
    ...defaultFilterDates(),
  }));

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [isOpen, setIsOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Espèces');
  const [paymentNote, setPaymentNote] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/installment-orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllData(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes :', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filteredData = useMemo(() => {
    return allData.filter((item) => {
      const search = appliedFilters.search.trim().toLowerCase();
      if (search && !item.designation?.toLowerCase().includes(search)) {
        return false;
      }

      const itemDate = new Date(item.created_at);

      if (appliedFilters.period && !matchesPeriod(itemDate, appliedFilters.period)) {
        return false;
      }

      if (appliedFilters.dateFrom) {
        const start = new Date(appliedFilters.dateFrom);
        start.setHours(0, 0, 0, 0);
        if (itemDate < start) return false;
      }

      if (appliedFilters.dateTo) {
        const end = new Date(appliedFilters.dateTo);
        end.setHours(23, 59, 59, 999);
        if (itemDate > end) return false;
      }

      return true;
    });
  }, [allData, appliedFilters]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredData];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;

    sorted.sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (key === 'last_payment_date') {
        aValue = a.last_payment?.created_at || '';
        bValue = b.last_payment?.created_at || '';
      }

      if (['total_amount', 'total_paid', 'remaining_amount', 'payment_count'].includes(key)) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedItems.slice(indexOfFirstItem, indexOfLastItem);

  const stats = useMemo(
    () => ({
      totalTtc: filteredData.reduce((t, i) => t + parseFloat(i.total_amount || 0), 0),
      totalPaid: filteredData.reduce((t, i) => t + parseFloat(i.total_paid || 0), 0),
      totalDue: filteredData.reduce((t, i) => t + parseFloat(i.remaining_amount || 0), 0),
      count: filteredData.length,
    }),
    [filteredData]
  );

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage((p) => p + 1);
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage((p) => p - 1);
    }
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters((prev) => ({
      ...prev,
      dateFrom: draftFilters.dateFrom,
      dateTo: draftFilters.dateTo,
      period: draftFilters.period,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setDraftFilters({ period: '', ...defaultFilterDates() });
    setAppliedFilters({ search: '', period: '', ...defaultFilterDates() });
    setCurrentPage(1);
    setSortConfig({ key: 'created_at', direction: 'desc' });
  };

  const openPaymentModal = (item) => {
    setSelectedOrder(item);
    setPaymentAmount('');
    setPaymentMethod('Espèces');
    setPaymentNote('');
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedOrder(null);
  };

  const handleDeleteOrder = async (item) => {
    const stockDeducted = item.stock_deducted !== false;
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Supprimer cette commande ?',
      html: stockDeducted
        ? 'Le stock des produits sera <strong>restauré</strong>. Les paiements associés seront également supprimés.'
        : 'Cette commande sera supprimée avec tous les paiements associés.',
      showCancelButton: true,
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#dc3545',
    });

    if (!result.isConfirmed) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${APP_API_URL}/installment-orders/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await fetchData();

      Swal.fire({
        icon: 'success',
        title: 'Supprimée',
        text: response.data?.message || 'Commande supprimée avec succès.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || 'Impossible de supprimer cette commande.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPayment = async () => {
    if (!selectedOrder || !paymentAmount) return;

    setPaymentLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${APP_API_URL}/installment-orders/${selectedOrder.id}/payments`,
        {
          amount: parseFloat(paymentAmount),
          user_id: user.id,
          payment_method: paymentMethod,
          notes: paymentNote,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      closePaymentModal();
      await fetchData();

      await Swal.fire({
        icon: 'success',
        title: 'Paiement ajouté avec succès',
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error('Erreur lors de l\'ajout du paiement :', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: "Une erreur s'est produite lors de l'ajout du paiement.",
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <MainLayout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Gestion des ventes</p>
          <h1 className="duo-hero-title">Ventes à crédit</h1>
          <p className="duo-hero-sub mt-2 mb-0">Suivez les commandes et encaissez les paiements échelonnés.</p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Total TTC (filtre)</p>
            <p className="duo-kpi-chip__value">{formatNumber(stats.totalTtc)} FCFA</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Montant payé</p>
            <p className="duo-kpi-chip__value">{formatNumber(stats.totalPaid)} FCFA</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Montant dû</p>
            <p className="duo-kpi-chip__value">{formatNumber(stats.totalDue)} FCFA</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Commandes</p>
            <p className="duo-kpi-chip__value">{stats.count}</p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des ventes à crédit
            </span>
            <div className="duo-toolbar__actions">
              <button
                type="button"
                title="Rafraîchir"
                onClick={fetchData}
                className="duo-btn duo-btn--ghost duo-btn--icon"
              >
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setIsOpen(!isOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${isOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${isOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher par référence..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {isOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <div className="duo-filters__grid">
                  <input
                    type="date"
                    className="duo-input"
                    title="Date début"
                    value={draftFilters.dateFrom}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                  />
                  <input
                    type="date"
                    className="duo-input"
                    title="Date fin"
                    value={draftFilters.dateTo}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                  />
                  <select
                    className="duo-select"
                    value={draftFilters.period}
                    onChange={(e) =>
                      setDraftFilters((prev) => ({
                        ...prev,
                        period: e.target.value,
                        dateFrom: e.target.value ? '' : prev.dateFrom,
                        dateTo: e.target.value ? '' : prev.dateTo,
                      }))
                    }
                  >
                    <option value="">Période rapide</option>
                    <option value="today">Aujourd&apos;hui</option>
                    <option value="yesterday">Hier</option>
                    <option value="this-week">Cette semaine</option>
                    <option value="this-month">Ce mois</option>
                    <option value="this-quarter">Ce trimestre</option>
                    <option value="this-year">Cette année</option>
                  </select>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('designation')}>
                    Référence <SortIcon active={isSortActive('designation')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('total_amount')}>
                    Montant TTC <SortIcon active={isSortActive('total_amount')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('total_paid')}>
                    Montant payé <SortIcon active={isSortActive('total_paid')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('remaining_amount')}>
                    Montant dû <SortIcon active={isSortActive('remaining_amount')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('payment_count')}>
                    Paiements <SortIcon active={isSortActive('payment_count')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('last_payment_date')}>
                    Dernier paiement <SortIcon active={isSortActive('last_payment_date')} direction={sortConfig.direction} />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={`installment-${item.id}`}>
                      <td className="fw-bold">{item.designation || 'N/A'}</td>
                      <td>{formatNumber(item.total_amount)} FCFA</td>
                      <td>{formatNumber(item.total_paid)} FCFA</td>
                      <td className="fw-bold" style={{ color: '#006633' }}>
                        {formatNumber(item.remaining_amount)} FCFA
                      </td>
                      <td>
                        <span className="duo-badge-pill duo-badge-pill--info">{item.payment_count || 0}</span>
                      </td>
                      <td className="text-nowrap">
                        {item.last_payment?.created_at
                          ? formatDateWithTime(item.last_payment.created_at)
                          : 'Aucun paiement'}
                      </td>
                      <td>
                        <div className="duo-table__actions">
                          <Link
                            to={`/admin/installment-orders/${item.id}`}
                            title="Facture"
                            className="duo-btn duo-btn--ghost duo-btn--sm"
                          >
                            <i className="bi bi-printer" />
                          </Link>
                          {!isRole13 && (
                            <button
                              type="button"
                              title="Ajouter un paiement"
                              className="duo-btn duo-btn--primary duo-btn--sm"
                              onClick={() => openPaymentModal(item)}
                            >
                              <i className="bi bi-plus-circle" /> Paiement
                            </button>
                          )}
                          {!isRole13 && (
                            <button
                              type="button"
                              title={item.stock_deducted !== false ? 'Supprimer et restaurer le stock' : 'Supprimer'}
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                              style={{ color: '#dc3545', borderColor: '#dc3545' }}
                              onClick={() => handleDeleteOrder(item)}
                            >
                              <i className="bi bi-trash" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="duo-empty-row">
                      Aucune commande correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentItems.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {indexOfFirstItem + 1} – {Math.min(indexOfLastItem, sortedItems.length)} sur {sortedItems.length}{' '}
                entrée{sortedItems.length > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {totalPages}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>

      {showPaymentModal && selectedOrder && (
        <DuoModal
          title={`Paiement — ${selectedOrder.designation}`}
          onClose={closePaymentModal}
          footer={
            <>
              <button type="button" className="duo-btn duo-btn--ghost" onClick={closePaymentModal} disabled={paymentLoading}>
                Annuler
              </button>
              <button
                type="button"
                className="duo-btn duo-btn--primary"
                onClick={handleAddPayment}
                disabled={paymentLoading || !paymentAmount}
              >
                {paymentLoading ? 'En cours...' : 'Valider'}
              </button>
            </>
          }
        >
          <div className="duo-balance-box mb-3">
            Reste à payer : <strong>{formatNumber(selectedOrder.remaining_amount)} FCFA</strong>
          </div>
          <div className="duo-form-group">
            <label htmlFor="paymentAmount" className="duo-form-label">
              Montant à payer *
            </label>
            <input
              type="number"
              id="paymentAmount"
              className="duo-input"
              min="0"
              step="1"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
            />
          </div>
          <div className="duo-form-group">
            <label htmlFor="paymentMethod" className="duo-form-label">
              Méthode de paiement *
            </label>
            <select
              id="paymentMethod"
              className="duo-select"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.label}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div className="duo-form-group mb-0">
            <label htmlFor="paymentNote" className="duo-form-label">
              Note (optionnelle)
            </label>
            <textarea
              id="paymentNote"
              className="duo-input"
              rows={3}
              value={paymentNote}
              onChange={(e) => setPaymentNote(e.target.value)}
              placeholder="Remarque ou commentaire"
            />
          </div>
        </DuoModal>
      )}
    </MainLayout>
  );
}
